import React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Input, SearchSelect, Text } from "@zorni/components-lib";
import styles from "./ProgramStepFormCard.module.css";
import HotelStars from "../../../../common/hotelStars/HotelStars";
import UploadImageField from "./UploadImageField";
import { useCityAutocomplete } from "../../../../../app/hooks/useCityAutocomplete";

const ProgramStepFormCard = ({
  editingStepIndex,
  upsertStep,
  validationError,
  stepErrors,
  stepDraft,
  updateDraft,
  addSubItem,
  expandedDraftActivities,
  toggleDraftItemExpand,
  removeSubItem,
  updateSubField,
  toggleActivityIncluded,
  updateSubPrice,
  expandedDraftOptions,
}) => {
  const { t } = useTranslation();
  const [destinationQuery, setDestinationQuery] = useState("");
  const {
    results: cityResults,
    loading: isCityLoading,
    hasRequested: hasCityRequested,
  } = useCityAutocomplete(destinationQuery);

  useEffect(() => {
    setDestinationQuery(stepDraft?.city || "");
  }, [stepDraft?.city]);

  const destinationOptions = useMemo(() => {
    const remoteOptions = (cityResults || []).map((item) => ({
      label: item.country ? `${item.city}, ${item.country}` : item.city,
      value: item.city,
      city: item.city,
      country: item.country || "",
      lat: item.lat,
      lng: item.lng,
    }));

    const currentCity = stepDraft?.city || "";
    const currentCountry = stepDraft?.country || "";
    if (!currentCity) return remoteOptions;

    const hasCurrentOption = remoteOptions.some((option) => option.value === currentCity);
    if (hasCurrentOption) return remoteOptions;

    return [
      {
        label: currentCountry ? `${currentCity}, ${currentCountry}` : currentCity,
        value: currentCity,
        city: currentCity,
        country: currentCountry,
        lat: stepDraft?.latitude,
        lng: stepDraft?.longitude,
      },
      ...remoteOptions,
    ];
  }, [cityResults, stepDraft?.city, stepDraft?.country, stepDraft?.latitude, stepDraft?.longitude]);
  const destinationInputKey = `${editingStepIndex ?? "new"}-${stepDraft?.city || ""}-${stepDraft?.country || ""}`;
  const toNumberOrNull = (value) =>
    Number.isFinite(Number(value)) ? Number(value) : null;
  const normalizeText = (value) => String(value || "").trim().toLowerCase();
  const findDestinationOption = (rawValue) => {
    const needle = normalizeText(rawValue);
    if (!needle) return null;
    const matches = destinationOptions.filter(
      (entry) =>
        normalizeText(entry.value) === needle ||
        normalizeText(entry.label) === needle ||
        normalizeText(entry.city) === needle
    );
    if (!matches.length) return null;
    return (
      matches.find(
        (entry) => toNumberOrNull(entry.lat) !== null && toNumberOrNull(entry.lng) !== null
      ) || matches[0]
    );
  };

  return (
    <div className={styles.sectionCard}>
      <div className={styles.sectionTop}>
        <Text
          as="h3"
          className={styles.blockTitle}
          variant="heading"
          size="lg"
          weight="bold"
        >
          {t("commons.labels.programStep")}
        </Text>
        <div className={styles.sectionTopActions}>
          <Text as="span" className={styles.stepBadge} size="xs" weight="bold">
            <Icon name="iconInfo" size="sm" className={styles.stepBadgeIcon} />
            {editingStepIndex !== null
              ? `${t("tripPublication.addTrip.program.editingStep")} ${editingStepIndex + 1}`
              : t("tripPublication.addTrip.program.newStep")}
          </Text>
          <Button
            onClick={upsertStep}
            label={
              editingStepIndex !== null
                ? t("commons.allActions.save")
                : t("tripPublication.addTrip.program.addStep")
            }
            variant="primary"
            size="small"
          />
        </div>
      </div>
      <div className={styles.grid}>
        <div className={styles.formGroup}>
          <Input
            floatingLabel
            helperText=""
            inputSize="medium"
            label={t("commons.labels.ranking.title")}
            name="rankingTitle"
            placeholder={t("commons.labels.ranking.title")}
            type="text"
            value={stepDraft.rankingTitle}
            onChange={(e) => updateDraft("rankingTitle", e.target.value)}
            errors={stepErrors?.rankingTitle}
          />
        </div>
        <div className={styles.formGroup}>
          <SearchSelect t={t}
            key={destinationInputKey}
            floatingLabel
            inputSize="medium"
            label={t("commons.labels.destination", { defaultValue: "Destination" })}
            name="stepDestination"
            placeholder={t("commons.labels.destination", { defaultValue: "Destination" })}
            id="stepDestination"
            value={destinationQuery}
            options={destinationOptions}
            isLoading={isCityLoading}
            className={
              hasCityRequested || isCityLoading
                ? styles.destinationSelect
                : `${styles.destinationSelect} ${styles.destinationSelectNoResults}`
            }
            onInput={(e) => {
              const next = e?.target?.value ?? "";
              setDestinationQuery(next);
            }}
            onChange={(value, option) => {
              const rawValue =
                typeof value === "string" ? value : value?.value || value?.label || "";
              const optionCity = option?.city || option?.value || option?.label || "";
              const matched = findDestinationOption(optionCity) || findDestinationOption(rawValue);
              const resolvedCity = optionCity || matched?.city || matched?.value || "";
              const resolvedCountry = option?.country || matched?.country || "";
              const resolvedLat = toNumberOrNull(option?.lat ?? option?.latitude ?? matched?.lat);
              const resolvedLng = toNumberOrNull(option?.lng ?? option?.longitude ?? matched?.lng);
              const sameCity =
                normalizeText(resolvedCity) === normalizeText(stepDraft?.city);

              if (resolvedCity) {
                setDestinationQuery(resolvedCity);
                updateDraft("city", resolvedCity);
                updateDraft("country", resolvedCountry);
                updateDraft(
                  "latitude",
                  resolvedLat !== null
                    ? resolvedLat
                    : sameCity
                    ? toNumberOrNull(stepDraft?.latitude)
                    : null
                );
                updateDraft(
                  "longitude",
                  resolvedLng !== null
                    ? resolvedLng
                    : sameCity
                    ? toNumberOrNull(stepDraft?.longitude)
                    : null
                );
                return;
              }

              const [cityPart, ...countryParts] = String(rawValue).split(",");
              const parsedCity = cityPart?.trim() || "";
              const parsedCountry = countryParts.join(",").trim();
              setDestinationQuery(parsedCity);
              updateDraft("city", parsedCity);
              updateDraft("country", parsedCountry);
              updateDraft("latitude", null);
              updateDraft("longitude", null);
            }}
          />
          {stepErrors?.city?.length || stepErrors?.country?.length ? (
            <Text as="span" size="xs" className={styles.fieldError}>
              {stepErrors?.city?.[0] || stepErrors?.country?.[0]}
            </Text>
          ) : null}
        </div>
        <div className={`${styles.formGroup} ${styles.full}`}>
          <div className={styles.mediaHostingRow}>
            <div className={styles.formGroup}>
              <UploadImageField
                label={t("commons.labels.picture")}
                value={stepDraft.pictureLink}
                autoUpload={true}
                onChange={(nextUrl) => {
                  updateDraft("pictureLink", nextUrl);
                  if (!nextUrl) {
                    updateDraft("isMain", false);
                  }
                }}
              />
            </div>
            <div className={styles.hostingColumn}>
              <div className={styles.hostingRow}>
                <label className={`${styles.toggleField} ${styles.stayRow}`} htmlFor="step-stay">
                  <input
                    className={styles.toggleInput}
                    id="step-stay"
                    type="checkbox"
                    checked={stepDraft.stay}
                    onChange={(e) => updateDraft("stay", e.target.checked)}
                  />
                  <span className={styles.toggleTrack} aria-hidden="true">
                    <span className={styles.toggleThumb} />
                  </span>
                  <Text as="span">{t("commons.labels.stay")}</Text>
                </label>
              </div>
              <div className={styles.hostingRow}>
                <Input
                  floatingLabel
                  helperText=""
                  inputSize="medium"
                  label={t("commons.labels.host")}
                  name="hostingName"
                  placeholder={t("commons.labels.host")}
                  type="text"
                  value={stepDraft.hostingName}
                  onChange={(e) => updateDraft("hostingName", e.target.value)}
                  disabled={!stepDraft.stay}
                  errors={stepErrors?.hostingName}
                />
              </div>
              <div className={styles.hostingRow}>
                <Input
                  helperText=""
                  inputSize="medium"
                  label={t("commons.labels.stars")}
                  name="hostingStars"
                  errors={stepErrors?.hostingStars}
                  forceFloating
                  className={styles.starsInputNoBorder}
                  customContent={
                    <HotelStars
                      name="hostingStars"
                      rating={stepDraft.hostingStars || 0}
                      setRating={(value) => updateDraft("hostingStars", value)}
                      disabled={!stepDraft.stay}
                    />
                  }
                />
              </div>
            </div>
          </div>
        </div>
        {stepDraft.pictureLink ? (
          <div className={`${styles.formGroup} ${styles.full}`}>
            <label className={styles.toggleField} htmlFor="step-main-picture">
              <input
                className={styles.toggleInput}
                id="step-main-picture"
                type="checkbox"
                checked={stepDraft.isMain}
                onChange={(e) => updateDraft("isMain", e.target.checked)}
              />
              <span className={styles.toggleTrack} aria-hidden="true">
                <span className={styles.toggleThumb} />
              </span>
              <Text as="span">{t("commons.labels.mainPicture")}</Text>
            </label>
          </div>
        ) : null}
        <div
          className={`${styles.formGroup} ${styles.full} ${styles.floatingTextAreaGroup}`}
        >
          {/* <textarea
            className={`${styles.textArea} ${styles.floatingTextArea}`}
            rows={4}
            value={stepDraft.description}
            onChange={(e) => updateDraft("description", e.target.value)}
            placeholder=" "
          />
          <Text as="label" className={styles.floatingTextAreaLabel}>{t("commons.labels.description")}</Text> */}
          <Input
            floatingLabel
            inputSize="medium"
            label={t("commons.labels.description")}
            multiline
            name="stepDescription"
            onChange={(e) => updateDraft("description", e.target.value)}
            rows={4}
            value={stepDraft.description}
          />
        </div>
      </div>

      <div className={styles.subContainer}>
        <div className={styles.subHeader}>
          <Text as="h4" weight="bold">
            {t("commons.labels.activities")}
          </Text>
          <Button
            onClick={() => addSubItem("activities")}
            label={t("tripPublication.addTrip.program.addActivity")}
            variant="secondary"
            size="small"
          />
        </div>
        {stepDraft.activities.map((item, idx) => (
          <div key={`a-${idx}`} className={styles.subItem}>
            <div className={styles.subItemHeader}>
              <Text as="span" className={styles.subItemTitle} weight="bold">
                {item.title || `${t("commons.labels.activity")} ${idx + 1}`}
              </Text>
              <div className={styles.subItemActions}>

                  <Icon
                    name={
                      expandedDraftActivities[idx] === false
                        ? "iconChevronDown"
                        : "iconChevronUp"
                    }
                    size="sm"
                    onClick={() => toggleDraftItemExpand("activities", idx)}
                  />

                  <Icon name="iconTrash" size="sm" onClick={() => removeSubItem("activities", idx)}/>

              </div>
            </div>
            {expandedDraftActivities[idx] !== false && (
              <div className={styles.grid}>
                <div className={`${styles.formGroup} ${styles.full}`}>
                  <div className={styles.subTopRow}>
                    <div className={styles.subLeftColumn}>
                      <div className={styles.formGroup}>
                        <Input
                          floatingLabel
                          helperText=""
                          inputSize="medium"
                          label={t("commons.labels.title")}
                          name={`activityTitle-${idx}`}
                          placeholder={t("commons.labels.title")}
                          type="text"
                          value={item.title}
                          onChange={(e) =>
                            updateSubField(
                              "activities",
                              idx,
                              "title",
                              e.target.value,
                            )
                          }
                          errors={stepErrors?.activities?.[idx]?.title}
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <UploadImageField
                          label={t("commons.labels.picture")}
                          value={item.picture}
                          autoUpload={true}
                          onChange={(nextUrl) =>
                            updateSubField(
                              "activities",
                              idx,
                              "picture",
                              nextUrl,
                            )
                          }
                        />
                      </div>
                    </div>
                    <div
                      className={`${styles.subRightColumn} ${styles.floatingTextAreaGroup}`}
                    >
                      {/* <textarea
                        className={`${styles.textArea} ${styles.floatingTextArea}`}
                        rows={8}
                        value={item.description}
                        onChange={(e) =>
                          updateSubField(
                            "activities",
                            idx,
                            "description",
                            e.target.value,
                          )
                        }
                        placeholder=" "
                      />
                      <Text as="label" className={styles.floatingTextAreaLabel}>
                        {t("commons.labels.description")}
                      </Text> */}
                      <Input
                        floatingLabel
                        inputSize="medium"
                        label={t("commons.labels.description")}
                        multiline
                        name={`activityDescription-${idx}`}
                        rows={8}
                        value={item.description}
                        onChange={(e) =>
                          updateSubField(
                            "activities",
                            idx,
                            "description",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className={`${styles.formGroup} ${styles.full}`}>
                  <label className={styles.toggleField} htmlFor={`activity-included-${idx}`}>
                    <input
                      className={styles.toggleInput}
                      id={`activity-included-${idx}`}
                      type="checkbox"
                      checked={Boolean(item.isIncluded)}
                      onChange={(e) =>
                        toggleActivityIncluded(idx, e.target.checked)
                      }
                    />
                    <span className={styles.toggleTrack} aria-hidden="true">
                      <span className={styles.toggleThumb} />
                    </span>
                    <Text as="span">{t("commons.labels.included")}</Text>
                  </label>
                </div>
                {!item.isIncluded && (
                  <>
                    <div className={styles.formGroup}>
                      <Input
                        floatingLabel
                        helperText=""
                        inputSize="medium"
                        label={t("commons.labels.currency")}
                        name={`activityCurrency-${idx}`}
                        placeholder={t("commons.labels.currency")}
                        type="text"
                        disabled
                        value={item.adultPricePerPerson.currency}
                        onChange={(e) => {
                          const c = e.target.value;
                          updateSubPrice(
                            "activities",
                            idx,
                            "adultPricePerPerson",
                            "currency",
                            c,
                          );
                          updateSubPrice(
                            "activities",
                            idx,
                            "childPricePerPerson",
                            "currency",
                            c,
                          );
                          updateSubPrice(
                            "activities",
                            idx,
                            "babiesPricePerPerson",
                            "currency",
                            c,
                          );
                        }}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <Input
                        floatingLabel
                        helperText=""
                        inputSize="medium"
                        label={t("commons.labels.adultPrice")}
                        name={`activityAdultPrice-${idx}`}
                        placeholder={t("commons.labels.adultPrice")}
                        type="number"
                        value={item.adultPricePerPerson.value}
                        onChange={(e) =>
                          updateSubPrice(
                            "activities",
                            idx,
                            "adultPricePerPerson",
                            "value",
                            e.target.value,
                          )
                        }
                        errors={
                          stepErrors?.activities?.[idx]?.adultPricePerPerson
                        }
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <Input
                        floatingLabel
                        helperText=""
                        inputSize="medium"
                        label={t("commons.labels.childPrice")}
                        name={`activityChildPrice-${idx}`}
                        placeholder={t("commons.labels.childPrice")}
                        type="number"
                        value={item.childPricePerPerson.value}
                        onChange={(e) =>
                          updateSubPrice(
                            "activities",
                            idx,
                            "childPricePerPerson",
                            "value",
                            e.target.value,
                          )
                        }
                        errors={
                          stepErrors?.activities?.[idx]?.childPricePerPerson
                        }
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <Input
                        floatingLabel
                        helperText=""
                        inputSize="medium"
                        label={t("commons.labels.babyPrice")}
                        name={`activityBabyPrice-${idx}`}
                        placeholder={t("commons.labels.babyPrice")}
                        type="number"
                        value={item.babiesPricePerPerson.value}
                        onChange={(e) =>
                          updateSubPrice(
                            "activities",
                            idx,
                            "babiesPricePerPerson",
                            "value",
                            e.target.value,
                          )
                        }
                        errors={
                          stepErrors?.activities?.[idx]?.babiesPricePerPerson
                        }
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className={styles.subContainer}>
        <div className={styles.subHeader}>
          <Text as="h4" weight="bold">
            {t("commons.labels.options")}
          </Text>
          <Button
            onClick={() => addSubItem("options")}
            label={t("tripPublication.addTrip.program.addOption")}
            variant="secondary"
            size="small"
          />
        </div>
        {stepDraft.options.map((item, idx) => (
          <div key={`o-${idx}`} className={styles.subItem}>
            <div className={styles.subItemHeader}>
              <Text as="span" className={styles.subItemTitle} weight="bold">
                {item.title || `${t("commons.labels.option")} ${idx + 1}`}
              </Text>
              <div className={styles.subItemActions}>
                  <Icon
                    name={
                      expandedDraftOptions[idx] === false
                        ? "iconChevronDown"
                        : "iconChevronUp"
                    }
                    size="sm"
                    onClick={() => toggleDraftItemExpand("options", idx)}
                  />
                  <Icon name="iconTrash" size="sm" onClick={() => removeSubItem("options", idx)}/>

              </div>
            </div>
            {expandedDraftOptions[idx] !== false && (
              <div className={styles.grid}>
                <div className={`${styles.formGroup} ${styles.full}`}>
                  <div className={styles.subTopRow}>
                    <div className={styles.subLeftColumn}>
                      <div className={styles.formGroup}>
                        <Input
                          floatingLabel
                          helperText=""
                          inputSize="medium"
                          label={t("commons.labels.title")}
                          name={`optionTitle-${idx}`}
                          placeholder={t("commons.labels.title")}
                          type="text"
                          value={item.title}
                          onChange={(e) =>
                            updateSubField(
                              "options",
                              idx,
                              "title",
                              e.target.value,
                            )
                          }
                          errors={stepErrors?.options?.[idx]?.title}
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <UploadImageField
                          label={t("commons.labels.picture")}
                          value={item.picture}
                          autoUpload={true}
                          onChange={(nextUrl) =>
                            updateSubField("options", idx, "picture", nextUrl)
                          }
                        />
                      </div>
                    </div>
                    <div
                      className={`${styles.subRightColumn} ${styles.floatingTextAreaGroup}`}
                    >
                      {/* <textarea
                        className={`${styles.textArea} ${styles.floatingTextArea}`}
                        rows={8}
                        value={item.description}
                        onChange={(e) =>
                          updateSubField(
                            "options",
                            idx,
                            "description",
                            e.target.value,
                          )
                        }
                        placeholder=" "
                      />
                      <Text as="label" className={styles.floatingTextAreaLabel}>
                        {t("commons.labels.description")}
                      </Text> */}
                      <Input
                        floatingLabel
                        inputSize="medium"
                        label={t("commons.labels.description")}
                        multiline
                        name={`optionDescription-${idx}`}
                        rows={8}
                        value={item.description}
                        onChange={(e) =>
                          updateSubField(
                            "options",
                            idx,
                            "description",
                            e.target.value,
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <Input
                    floatingLabel
                    helperText=""
                    inputSize="medium"
                    label={t("commons.labels.currency")}
                    name={`optionCurrency-${idx}`}
                    placeholder={t("commons.labels.currency")}
                    type="text"
                    disabled
                    value={item.adultPricePerPerson.currency}
                    onChange={(e) => {
                      const c = e.target.value;
                      updateSubPrice(
                        "options",
                        idx,
                        "adultPricePerPerson",
                        "currency",
                        c,
                      );
                      updateSubPrice(
                        "options",
                        idx,
                        "childPricePerPerson",
                        "currency",
                        c,
                      );
                      updateSubPrice(
                        "options",
                        idx,
                        "babiesPricePerPerson",
                        "currency",
                        c,
                      );
                    }}
                  />
                </div>
                <div className={styles.formGroup}>
                  <Input
                    floatingLabel
                    helperText=""
                    inputSize="medium"
                    label={t("commons.labels.adultPrice")}
                    name={`optionAdultPrice-${idx}`}
                    placeholder={t("commons.labels.adultPrice")}
                    type="number"
                    value={item.adultPricePerPerson.value}
                    onChange={(e) =>
                      updateSubPrice(
                        "options",
                        idx,
                        "adultPricePerPerson",
                        "value",
                        e.target.value,
                      )
                    }
                    errors={stepErrors?.options?.[idx]?.adultPricePerPerson}
                  />
                </div>
                <div className={styles.formGroup}>
                  <Input
                    floatingLabel
                    helperText=""
                    inputSize="medium"
                    label={t("commons.labels.childPrice")}
                    name={`optionChildPrice-${idx}`}
                    placeholder={t("commons.labels.childPrice")}
                    type="number"
                    value={item.childPricePerPerson.value}
                    onChange={(e) =>
                      updateSubPrice(
                        "options",
                        idx,
                        "childPricePerPerson",
                        "value",
                        e.target.value,
                      )
                    }
                    errors={stepErrors?.options?.[idx]?.childPricePerPerson}
                  />
                </div>
                <div className={styles.formGroup}>
                  <Input
                    floatingLabel
                    helperText=""
                    inputSize="medium"
                    label={t("commons.labels.babyPrice")}
                    name={`optionBabyPrice-${idx}`}
                    placeholder={t("commons.labels.babyPrice")}
                    type="number"
                    value={item.babiesPricePerPerson.value}
                    onChange={(e) =>
                      updateSubPrice(
                        "options",
                        idx,
                        "babiesPricePerPerson",
                        "value",
                        e.target.value,
                      )
                    }
                    errors={stepErrors?.options?.[idx]?.babiesPricePerPerson}
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProgramStepFormCard;
